import React, { useCallback, useEffect, useState } from "react";
import Api from "../../api";
import { hasUserRole } from "../../utils";
import ContactPanel from "./ContactPanel";
import "./contactPanel.css";

const Tabs = () => {
  const [contacts, setContacts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const isAdmin = hasUserRole(["ROLE_ADMIN"]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await Api.Contact.GetAll();
      const list = Array.isArray(data) ? data : [];
      setContacts(list);
      setActiveId((currentId) => {
        if (currentId && list.some((item) => String(item.id) === currentId)) {
          return currentId;
        }
        return list.length > 0 ? String(list[0].id) : null;
      });
    } catch (err) {
      setContacts([]);
      setActiveId(null);
      setError(err.message || "Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleContactUpdated = useCallback((updatedContact) => {
    if (!updatedContact?.id) return;
    setContacts((prev) =>
      prev.map((item) =>
        item.id === updatedContact.id ? updatedContact : item
      )
    );
  }, []);

  const handleEditModeToggle = () => {
    setIsEditMode((prev) => !prev);
  };

  const activeContact = contacts.find(
    (contact) => String(contact.id) === activeId
  );

  if (loading) {
    return (
      <div className="contact-layout contact-layout-status">
        <p className="contact-status-message">Cargando contactos…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-layout contact-layout-status">
        <p className="contact-status-message contact-status-error">{error}</p>
        <button
          type="button"
          className="contact-retry-btn"
          onClick={loadContacts}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="contact-layout contact-layout-status">
        <p className="contact-status-message">No hay contactos disponibles.</p>
      </div>
    );
  }

  return (
    <div className="contact-page-wrap">
      {isAdmin && (
        <div className="contact-admin-bar">
          <p className="contact-admin-bar-text">
            {isEditMode
              ? "Estás editando la información de contacto."
              : "Vista de lectura. Activa el modo edición para modificar contactos."}
          </p>
          <button
            type="button"
            className={`contact-edit-toggle ${
              isEditMode ? "contact-edit-toggle-active" : ""
            }`}
            onClick={handleEditModeToggle}
            aria-pressed={isEditMode}
          >
            {isEditMode ? "Desactivar edición" : "Activar edición"}
          </button>
        </div>
      )}

      <div className="contact-layout">
        <nav className="contact-sidebar" aria-label="Contactos">
          <h2 className="contact-sidebar-title">Contactos</h2>

          <div className="contact-location-desktop">
            {contacts.map((contact) => (
              <button
                key={contact.id}
                type="button"
                className={`contact-location-btn ${
                  activeId === String(contact.id) ? "active" : ""
                }`}
                onClick={() => setActiveId(String(contact.id))}
              >
                {contact.name}
              </button>
            ))}
          </div>

          <label className="contact-location-mobile" htmlFor="contact-select">
            <span className="contact-location-mobile-label">Contacto</span>
            <select
              id="contact-select"
              className="contact-location-select"
              value={activeId ?? ""}
              onChange={(e) => setActiveId(e.target.value)}
            >
              {contacts.map((contact) => (
                <option key={contact.id} value={String(contact.id)}>
                  {contact.name}
                </option>
              ))}
            </select>
          </label>
        </nav>
        <div className="contact-content">
          <ContactPanel
            contact={activeContact}
            editMode={isAdmin && isEditMode}
            onContactUpdated={handleContactUpdated}
          />
        </div>
      </div>
    </div>
  );
};

export default Tabs;
